import type { useTranslation } from 'react-i18next';

import type { SidebarGroup, SidebarItem } from '@/components/shell/Sidebar';

import {
  PATIENT_RECORD_SECTIONS,
  PATIENT_SECTION_GROUP_ORDER,
  type PatientSectionGroup,
} from '../sections.config';

type Translator = ReturnType<typeof useTranslation>['t'];

const GROUP_HEADING_KEY: Record<PatientSectionGroup, string> = {
  identity: 'routes.patient.sidebar.identity',
  clinical: 'routes.patient.sidebar.clinical',
  care: 'routes.patient.sidebar.care',
  governance: 'routes.patient.sidebar.governance',
};

/**
 * Sidebar groups for the patient record route.
 *
 * The patient record is a single scrollable column with anchor-linked
 * sections; the sidebar is a "jump to section" table of contents derived
 * from `PATIENT_RECORD_SECTIONS`. Adding a section is a single registry
 * entry — this file just buckets entries into groups.
 *
 * Pedigree is now a regular `#pedigree` content section in the registry
 * (PTV2-1148): it lists the person's pedigrees as tiles and opens the
 * full-canvas editor from there, so it no longer needs a special
 * sidebar route entry.
 */
export function patientSidebarGroups(
  guid: string,
  t: Translator,
): readonly SidebarGroup[] {
  const base = `/patients/${guid}`;

  // The first section in the registry (currently Demographics under
  // identity) is the implicit default — the patient record renders it
  // when there's no `#section-id` hash. Mark its sidebar entry as
  // `defaultActive` so the highlight stays correct on fresh navigation
  // before the user has chosen a section.
  const defaultSectionId = PATIENT_RECORD_SECTIONS[0]?.id;

  const sectionGroups = PATIENT_SECTION_GROUP_ORDER.map<SidebarGroup>((group) => {
    const items = PATIENT_RECORD_SECTIONS.filter(
      (section) => section.group === group,
    ).map<SidebarItem>((section) => ({
      key: section.id,
      label: t(section.labelKey),
      to: `${base}#${section.id}`,
      icon: <section.Icon className="h-4 w-4" aria-hidden="true" />,
      ...(section.id === defaultSectionId ? { defaultActive: true } : {}),
    }));

    return {
      key: group,
      heading: t(GROUP_HEADING_KEY[group]),
      items,
    };
  });

  return sectionGroups;
}
